import React, { Fragment, Suspense, useRef } from "react";
import { Route, Routes, useLocation, useNavigate } from "react-router-dom";
import AddProductToCartForm from "../components/AddProductToCartForm";
import ErrorComponent from "../components/common/ErrorComponent";
import Loader from "../components/common/Loader";
import Modal from "../components/common/Modal";
import LoginForm from "../components/LoginForm";
import RegisterForm from "../components/RegisterForm";
import { errorModalToggle, setSignOutModal } from "../redux/actions/appActions";
import { useAppDispatch, useAppSelector } from "../redux/hook";
import { RESELLER_TOKEN } from "../utils/constants";
import { isServerCheckoutEnabled } from "../utils/featureFlags";
import { lazyWithRetry } from "../utils/lib";
import { setToggleCartModal, setToggleSignOut } from "../redux/slice/app.slice";
import {
  setAuthenticateUser,
  setIsAuthenticated,
  setUser,
} from "../redux/slice/auth.slice";
import OrderSuccess from "../pages/Cart/components/OrderSuccess";
import { clearCart } from "../redux/slice/cart.slice";
import VerifyUserOTP from "../pages/Accounts/components/VerifyUserOTP";
import AddProductToCollection from "../pages/Shop/addProductToCollection";
import Footer from "../components/common/Footer";
 
interface Props {}

const Home = lazyWithRetry(
  () => import(/* webpackChunkName: "home" */ "../pages/Home/Home")
);
const Categories = lazyWithRetry(
  () =>
    import(
      /* webpackChunkName: "categories" */ "../pages/Categories/Categories"
    )
);
const Category = lazyWithRetry(
  () =>
    import(
      /* webpackChunkName: "category" */ "../pages/Categories/components/CategoriesProducts"
    )
);
const NewArrivalsPage = lazyWithRetry(
  () =>
    import(
      /* webpackChunkName: "newArrivals"*/ "../pages/Home/components/NewArrivalsPage"
    )
);
const Shop = lazyWithRetry(
  () => import(/* webpackChunkName: "shop" */ "../pages/Shop")
);
const Leaderboard = lazyWithRetry(
  () => import(/* webpackChunkName: "shop" */ "../pages/LeaderBoard")
);


const VerifyEmail = lazyWithRetry (
  () => import(/* webpackChunkName: "verifyemail" */ "../pages/Accounts/components/EmailVerification")
)

const VerifyPhone = lazyWithRetry (
  () => import(/* webpackChunkName: "verifyemail" */ "../pages/Accounts/components/PhoneVerification")
)
const Explore = lazyWithRetry(
  () => import(/* webpackChunkName: "explore" */ "../pages/Explore/Explore")
);
// const Promo = lazyWithRetry(
//   () => import(/* webpackChunkName: "promo" */ "../pages/Promo/Promo")
// );
const PromoProducts = lazyWithRetry(
  () =>
    import(
      /* webpackChunkName: "promoProduct" */ "../pages/Promo/components/PromoProductList"
    )
);

const Sales = lazyWithRetry(
  () =>
    import(
      /* webpackChunkName: "sales" */ "../pages/Shop/sales"
    )
);

const MyCollections = lazyWithRetry(
  () =>
    import(/* webpackChunkName: "mycollections" */ "../pages/Shop/collection")
);

const ShowShopProducts = lazyWithRetry(
  () =>
    import(
      /* webpackChunkName: "showshopproducts" */ "../pages/Shop/showShopProducts"
    )
);

const Accounts = lazyWithRetry(
  () => import(/* webpackChunkName: "accounts" */ "../pages/Accounts/Accounts")
);
const Product = lazyWithRetry(
  () => import(/* webpackChunkName: "product" */ "../components/ProductDetails")
);
const Order = lazyWithRetry(
  () => import(/* webpackChunkName: "order" */ "../components/OrderForm")
);
const ConfirmOrder = lazyWithRetry(
  () =>
    import(/* webpackChunkName: "confirmOrder" */ "../components/OrderConfirm")
);
const UserOrders = lazyWithRetry(
  () =>
    import(
      /* webpackChunkName: "userOrders" */ "../pages/Accounts/components/UserOrders"
    )
);
const UserTopup = lazyWithRetry(
  () =>
    import(
      /* webpackChunkName: "userOrders" */ "../pages/Accounts/components/TopUpWallet"
    )
);
const ClaimCoupon = lazyWithRetry(
  () =>
    import(
      /* webpackChunkName: "userOrders" */ "../pages/Accounts/components/ClaimCoupon"
    )
);
const OrderDetails = lazyWithRetry(
  (): any =>
    import(
      /* webpackChunkName: "orderDetails" */ "../pages/Accounts/components/OrderDetails"
    )
);

const DigitalSummary = lazyWithRetry(
  (): any =>
    import(
      /* webpackChunkName: "digitalSummary" */"../pages/Cart/components/DigitalSummary")
);

const Wallet = lazyWithRetry(
  () =>
    import(
      /* webpackChunkName: "wallet" */ "../pages/Accounts/components/Wallet"
    )
);
const Customers = lazyWithRetry(
  () => import(/* webpackChunkName: "customer" */ "../pages/Customers")
);
const CustomerUpdate = lazyWithRetry(
  () => import(/* webpackChunkName: "customer" */ "../pages/Customers/CustomersModal")
);

const Settings = lazyWithRetry(
  () =>
    import(
      /* webpackChunkName: "settings" */ "../pages/Accounts/components/Settings"
    )
);
const RequestPayOut = lazyWithRetry(
  (): any =>
    import(
      /* webpackChunkName: "requestPayout" */ "../pages/Accounts/components/PayOutRequest"
    )
);
const ReferAFriend = lazyWithRetry(
  (): any =>
    import(
      /* webpackChunkName: "referAFriend" */ "../pages/Accounts/components/ReferAFriend"
    )
);
const TendoAcademy = lazyWithRetry(
  (): any =>
    import(
      /* webpackChunkName: "referAFriend" */ "../pages/Accounts/components/TendoAcademy"
    )
);
const DeliveryPrices = lazyWithRetry(
  (): any => import("../pages/Accounts/components/DeliveryLocation")
);

const Cart = lazyWithRetry(
  (): any => import(/* webpackChunkName: "Cart" */ "../pages/Cart/Cart")
);
// Server-authoritative checkout V2 pages, rendered when the server-checkout
// flag is on (docs/server-checkout-migration.md).
const CartV2 = lazyWithRetry(
  (): any => import(/* webpackChunkName: "CartV2" */ "../pages/Cart/v2/CartV2")
);
const CheckoutDetailsV2 = lazyWithRetry(
  (): any =>
    import(
      /* webpackChunkName: "CartV2" */ "../pages/Cart/v2/CheckoutDetailsV2"
    )
);
const CheckoutReviewV2 = lazyWithRetry(
  (): any =>
    import(
      /* webpackChunkName: "CartV2" */ "../pages/Cart/v2/CheckoutReviewV2"
    )
);
const CartCustomer = lazyWithRetry(
  (): any =>
    import(
      /* webpackChunkName: "Cart" */ "../pages/Cart/components/AddCustomer"
    )
);
const Checkout = lazyWithRetry(
  () => import("../pages/Cart/components/Checkout")
);
const DigitalCheckout = lazyWithRetry(
  () => import("../pages/Cart/components/DigitalCheckout")
);
const Auth = lazyWithRetry(() => import("../pages/Auth/Auth"));
const Onboarding = lazyWithRetry(
  () => import("../pages/Auth/components/Onboarding")
);
const LearnToSell = lazyWithRetry(
  () => import("../pages/Auth/components/LearnToSell")
);
const EarningPlan = lazyWithRetry(
  () => import("../pages/Auth/components/EarningPlan")
);
const SetGoal = lazyWithRetry(
  () => import("../pages/Auth/components/SetGoal")
);
const WelcomePage = lazyWithRetry(
  () => import("../pages/Auth/components/WelcomePage")
);
const Gallery = lazyWithRetry(() => import("../pages/Gallery/Gallery"));
const ResetPassword = lazyWithRetry(
  () => import("../components/ResetPassword")
);
const PaymentOTP = lazyWithRetry(
  () => import("../pages/Accounts/components/PayoutOTP")
);
const AddPayoutDetails = lazyWithRetry(
  () => import("../pages/Accounts/components/AddPayoutDetails")
);
const Notifications = lazyWithRetry(
  () => import("../pages/Accounts/components/Notification")
);
const ProductSearch = lazyWithRetry(
  () => import("../pages/Search/ProductSearch")
);
const Collection = lazyWithRetry(
  () => import("../pages/Collections/Collection")
);
const SupplierShop = lazyWithRetry(() => import("../pages/supplierShop"));

const AppLayout = (props: Props) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const {
    toggleSignOut,
    toggleErrorModal,
    toggleCartModal: openCartModal,
  } = useAppSelector((state) => state.app);
  const { user, authStatus } = useAppSelector((state) => state.auth);
  // const { promptUser } = useAppSelector((state) => state.cart);
  const refLink = new URLSearchParams(location.search);
  const refCode = refLink?.get("refCode");
  const token = localStorage.getItem(RESELLER_TOKEN) ?? null;

  const init = useRef({ dispatch, navigate });

  const isAuthPage = React.useMemo(() => {
    if (location.pathname.includes("auth")) {
      return true;
    }
    return false;
  }, [location.pathname]);

  React.useEffect(() => {
    const { dispatch, navigate } = init.current;

    if (!token && !user && !isAuthPage) {
      navigate(`/auth${refCode ? `?refCode=${refCode}` : ""}`);
    }

    if (token && !user) {
      dispatch(setAuthenticateUser(token));
    }
  }, [token, isAuthPage, user, refCode]);

  const signOut = () => {
    dispatch(setToggleSignOut(!toggleSignOut));  
    dispatch(setIsAuthenticated(false));
    dispatch(setUser(null));
    dispatch(clearCart());
  };

  return (
    <Fragment>
    <div className="min-h-screen flex flex-col">
        {/* Main content area that grows to fill available space */}
        <div className="flex-1">
          <Suspense fallback={<Loader />}>
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/categories" element={<Categories />} />
              <Route path="/shop" element={<Shop />} />
              <Route path="/shop/collections" element={<MyCollections />} />
              <Route
                path="/shop/showshopproducts"
                element={<ShowShopProducts />}
              />

              <Route path="/supplier-shop/:id" element={<SupplierShop />} />
              <Route path="/categories/:categoryName" element={<Category />} />
              <Route path="/newarrivals" element={<NewArrivalsPage />} />
              <Route path="/explore" element={<Explore />} />
              <Route path="/search" element={<ProductSearch />} />
              <Route path="/promotions/:promo" element={<PromoProducts />} />
              <Route path="/accounts" element={<Accounts />} />
              <Route path="cart">
                <Route
                  index
                  element={isServerCheckoutEnabled() ? <CartV2 /> : <Cart />}
                />
                <Route
                  path="customer"
                  element={
                    isServerCheckoutEnabled() ? (
                      <CheckoutDetailsV2 />
                    ) : (
                      <CartCustomer />
                    )
                  }
                />
                <Route
                  path="checkout"
                  element={
                    isServerCheckoutEnabled() ? (
                      <CheckoutReviewV2 />
                    ) : (
                      <Checkout />
                    )
                  }
                />
                <Route path="digital-checkout" element={<DigitalCheckout />} />
                   <Route path="digital/summary" element={<DigitalSummary />} />
                <Route path="success" element={<OrderSuccess />} />
              </Route>
              <Route path="/auth" element={<Auth />}>
                <Route index element={<WelcomePage />} />
                <Route path="login" element={<LoginForm />} />
                <Route path="register" element={<RegisterForm />} />
                <Route path="onboard" element={<Onboarding />} />
                <Route path="reset-password" element={<ResetPassword />} />
                <Route path="learn-to-sell" element={<LearnToSell />} />
                <Route path="earning-plan" element={<EarningPlan />} />
                <Route path="set-goal" element={<SetGoal />} />
               </Route>
              <Route path="/accounts/myorders" element={<UserOrders />} />
              <Route path="/accounts/topup" element={<UserTopup />} />
              <Route path="/accounts/claim-coupon" element={<ClaimCoupon />} />
              <Route path="/accounts/verify" element={<VerifyUserOTP />} />
              <Route path="/accounts/sales" element={<Sales/>}/>
              <Route path="/verify-email" element={<VerifyEmail/>}/>
              <Route path="/verify-phone" element={<VerifyPhone/>}/>
              <Route path="/customer" element={<CustomerUpdate/>}/>
              <Route path="/accounts/notifications" element={<Notifications />} />
              <Route
                path="/accounts/addpayoutdetails"
                element={<AddPayoutDetails />}
              />

              <Route
                path="/accounts/order/:orderNumber"
                element={<OrderDetails />}
              />
              <Route path="/accounts/settings" element={<Settings />} />
              <Route path="/accounts/wallet" element={<Wallet />} />
              <Route path="/accounts/customers" element={<Customers />} />
              <Route
                path="/accounts/wallet/request-payout"
                element={<RequestPayOut />}
              />
              <Route
                path="/accounts/wallet/otp-payout"
                element={<PaymentOTP />}
              />
              <Route
                path="/accounts/wallet/payout-details"
                element={<AddPayoutDetails />}
              />
              <Route path="/accounts/referral" element={<ReferAFriend />} />
              <Route path="/accounts/learn" element={<TendoAcademy />} />
              <Route
                path="/accounts/delivery-prices"
                element={<DeliveryPrices />}
              />
              <Route path="/product/:id" element={<Product />} />
              <Route path="/collections/:id" element={<Collection />} />
              <Route path="/product/order" element={<Order />} />
              <Route path="/product/gallery" element={<Gallery />} />
              <Route path="/order/confirm" element={<ConfirmOrder />} />
              <Route
                path="/collection/:collectionId"
                element={<AddProductToCollection />}
              />
              <Route path="/leaderboard" element={<Leaderboard/>}/>
            </Routes>
          </Suspense>
        </div>
            {!isAuthPage && <Footer />}
       </div>

      <Modal
        show={toggleErrorModal}
        setShow={() => dispatch(errorModalToggle(!toggleErrorModal))}
      >
        <ErrorComponent error={authStatus && authStatus.error} />
      </Modal>
      <Modal
        show={toggleSignOut}
        setShow={() => dispatch(setSignOutModal(!toggleSignOut))}
      >
        <div className="p-2 font-nunito">
          <h1 className="text-lg font-semibold text-gray-900 mb-2">
            Sign out
          </h1>
          <p className="text-sm text-gray-600 mb-4">
            Are you sure you want to sign out?
          </p>
          <div className="flex gap-3">
            <button
              type="button"
              className="flex-1 px-4 py-3 text-sm font-medium text-gray-800 bg-gray-100 rounded-xl"
              onClick={() => dispatch(setSignOutModal(false))}
            >
              Stay signed in
            </button>
            <button
              type="button"
              className="flex-1 px-4 py-3 text-sm font-semibold text-white bg-blue-500 rounded-xl"
              onClick={signOut}
            >
              Sign out
            </button>
          </div>
        </div>
      </Modal>
      {/* <Modal
        show={promptUser}
        setShow={() => dispatch(setUsePrompt(!promptUser))}
      >
        <div className="mt-2">
          <span className="text-sm text-gray-500">
            You have an active cart. Do you still want to add product?
          </span>
        </div>

        <div className="mt-4 flex justify-between">
          <button
            type="button"
            className="inline-flex justify-center px-4 py-2 text-xs md:text-sm font-medium text-blue-900 bg-blue-100 border border-transparent rounded-md hover:bg-blue-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-blue-500"
            onClick={() => dispatch(clearCart())}
          >
            No, reset cart
          </button>
          <button
            type="button"
            className="inline-flex justify-center px-4 py-2 text-xs md:text-sm font-medium text-blue-900 bg-gray-100 border border-transparent rounded-md focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-blue-500"
            onClick={() => {
              dispatch(setUsePrompt(!promptUser));
            }}
          >
            Yes, add to cart
          </button>
        </div>
      </Modal> */}
      <Modal
        show={openCartModal}
        setShow={() => dispatch(setToggleCartModal(!openCartModal))}
      >
        <AddProductToCartForm />
      </Modal>
    </Fragment>
  );
};

export default AppLayout;
